// crewbit-admin/src/lib/invoiceLineItems.ts
//
// Pure helpers for §InvoiceAuthoring line-item conversion + totals math.
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §InvoiceAuthoring.7.3.
//
// Boundary semantics (Hard rules #9 + #10):
//   - UI: dollars-as-string + tax percent (operator readability — "13" for 13%)
//   - RPC: cents (bigint) + tax decimal (0.13)
//   - Storage: numeric dollars + decimal tax_rate
//
// Mirrors crewbit-mobile/lib/invoiceLineItems.ts — admin imports from
// './money' (not '@/lib/money'); otherwise identical.

import {
  canonicalMoney,
  multiplyByPercent,
  sumMoney,
} from './money';

export interface AuthoringLine {
  id: string;
  description: string;
  quantity: number;
  unitPriceDollars: string;
  amountDollars: string;
  kind: 'material' | 'labor' | 'discount' | 'fee' | 'service' | null;
}

export interface RpcLineItem {
  description: string;
  amount_cents: number;
  quantity?: number;
  unit_price_cents?: number;
  kind?: 'material' | 'labor' | 'discount' | 'fee' | 'service';
}

export function linesToCents(lines: AuthoringLine[]): RpcLineItem[] {
  return lines.map((l) => {
    const item: RpcLineItem = {
      description: l.description.trim(),
      amount_cents: Math.round(parseFloat(l.amountDollars || '0') * 100),
      quantity: l.quantity,
      unit_price_cents: Math.round(parseFloat(l.unitPriceDollars || '0') * 100),
    };
    if (l.kind) item.kind = l.kind;
    return item;
  });
}

export function linesFromCents(rpcLines: RpcLineItem[]): AuthoringLine[] {
  return rpcLines.map((rl, idx) => ({
    id: `line-${idx}`,
    description: rl.description,
    quantity: rl.quantity ?? 1,
    unitPriceDollars:
      rl.unit_price_cents != null
        ? (rl.unit_price_cents / 100).toFixed(2)
        : (rl.amount_cents / 100).toFixed(2),
    amountDollars: (rl.amount_cents / 100).toFixed(2),
    kind: rl.kind ?? null,
  }));
}

export function subtotalDollars(lines: AuthoringLine[]): string {
  return sumMoney(lines.map((l) => canonicalMoney(l.amountDollars)));
}

export function taxDollars(subtotal: string, taxRatePercent: number): string {
  return multiplyByPercent(subtotal, taxRatePercent);
}
